
import { Component, Vue, } from 'vue-property-decorator';
import { validationMixin, } from 'vuelidate';
import { minLength, numeric, required, } from 'vuelidate/lib/validators';
import { isBadRequestResult, } from '~/api/error-helpers';
import { Store, } from '~/apps/account/store';
import FeedbackBox from '~/components/feedback-box/feedback-box.vue';
import HeaderText from '~/components/header-text/header-text.vue';
import PhoneNumberInput from '~/components/self-serve/phone-number-input/phone-number-input.vue';
import Tooltip from '~/components/tooltip/tooltip.vue';
import { preventLeaveMixin, } from '~/mixins/prevent-leave-mixin';
import numberOfOccupants from '~/utils/models/numberOfOccupants';
import { customerNumberLength, landLineNumberRules, phoneNumberRules, } from '~/utils/validators';

function forcePresent<ItemType>(item: ItemType): { [k in keyof ItemType]: NonNullable<ItemType[k]> } {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
	return item as any;
}

interface CountryListItem {
	readonly isdCode: string;
	readonly countryCode: string;
	readonly countryName: string;
}

@Component({
	components: {
		FeedbackBox,
		HeaderText,
		Tooltip,
		PhoneNumberInput,
	},
	metaInfo: {
		title: 'Register - Your details - Southern Water',
	},
	mixins: [
		preventLeaveMixin,
		validationMixin,
	],
	validations: {
		customerNumber: {
			required,
			numeric,
			customerNumberLength,
		},
		customerLastName: {
			required,
			minLength: minLength(2),
		},
		agreeTnc: {
			required,
		},
		phoneNumberOption1: phoneNumberRules,
		phoneNumberOption2: landLineNumberRules,
		numberOfOccupants: {
			required,
		},
	},
})
export default class RegisterYourDetails extends Vue {
	customerNumber = this.customerDetails?.customerNumber || this.findCustomerNumber.customerNumber || null;
	customerLastName = this.customerDetails?.customerLastName || this.findCustomerNumber.customerLastName || null;
	numberOfOccupants = this.customerDetails?.numberOfOccupants || null;
	phoneNumberOption1 = {
		countryCode: this.customerDetails?.phoneNumberOption1.countryCode || '+44',
		phoneNumber: this.customerDetails?.phoneNumberOption1.phoneNumber || this.findCustomerNumber.mobileDetails.phoneNumber || '',
	};

	phoneNumberOption2 = {
		countryCode: this.customerDetails?.phoneNumberOption2.countryCode || '+44',
		phoneNumber: this.customerDetails?.phoneNumberOption2.phoneNumber || '',
	};

	userData = {};

	registrationContent = Store.state.customContent.registrationContent;

	apiError: string | null = null;
	customCustomerNumError = false;
	displayTermsConditionsCheckBox = false;
	accept = false;
	agreeTnc = false;

	countryList: CountryListItem[] = [];

	personsToDisplayText: string | null = null;

	get customerDetails() {
		return Store.state.register.customerDetails;
	}

	get numberOfOccupantsList() {
		return numberOfOccupants;
	}

	get findCustomerNumber() {
		return Store.state.findCustomerNumber;
	}

	contactUsLocation = Store.getters.getGlobalConfigValue('ContactUsLocation');

	mounted() {
		Store.commit.register.setStep(1);
		const parameters = this.$route.query;
		if (parameters && parameters.token && typeof parameters.token === 'string') {
			this.populateUserDetails(parameters.token);
			this.displayTermsConditionsCheckBox = true;
		}

		this.$selfServeApi.countryListList()
			.then(({ result, }) => {
				this.countryList = result.sort((a, b) => a.countryName.localeCompare(b.countryName));
			})
			.catch((error) => {
				this.apiError = `Please wait a few minutes and try again. If the problem persists, please <a target="_blank" href=${this.contactUsLocation}>contact us</a>.`;
				if (isBadRequestResult(error) && Array.isArray(error.errors) && error.errors[0]?.message) {
					this.apiError = error.errors[0].message;
				}
			}).finally(() => Store.commit.hideSpinner());
	}

	checkboxToggle(event: Event) {
		this.accept = (event.target !== null) && (event.target as HTMLInputElement).checked;
	}

	populateUserDetails(token: string) {
		this.apiError = null;
		Store.commit.showSpinner();
		this.$selfServeApi.registerAccountCustomerInfoForPrePopulateCreate({
			activationCode: token,
		})
			.then((resp) => {
				const { customerNumber, lastName, } = resp.result;
				this.customerNumber = customerNumber;
				this.customerLastName = lastName;
			})
			.catch((error) => {
				if (isBadRequestResult(error) && Array.isArray(error.errors) && error.errors[0]?.message) {
					if (error.errors[0]?.metadata.Number === '0014') {
						sessionStorage.setItem('registerEmailToken', token);
						return this.$router.push('./link-expired');
					} else {
						this.apiError = error.errors[0].message;
					}
				}
			}).finally(() => Store.commit.hideSpinner());
	}

	handleSubmit() {
		this.apiError = null;
		this.customCustomerNumError = false;
		const currentForm = this.$v;
		currentForm.$touch();
		window.scrollTo(0, 0);
		if (currentForm.$pending || currentForm.$error || (this.displayTermsConditionsCheckBox && !currentForm.agreeTnc.$model)) {
			return;
		}

		const selectedIsdCodeOption1 = this.countryList.find((item) => item.isdCode === this.phoneNumberOption1.countryCode);
		const selectedIsdCodeOption2 = this.countryList.find((item) => item.isdCode === this.phoneNumberOption2.countryCode);
		const { customerLastName, customerNumber, numberOfOccupants, phoneNumberOption1, phoneNumberOption2, } = this;
		const userDetail = forcePresent({
			customerLastName,
			customerNumber,
			numberOfOccupants,
			phoneNumberOption1: { ...phoneNumberOption1, phoneCode: selectedIsdCodeOption1?.countryCode || 'GB', },
			phoneNumberOption2: { ...phoneNumberOption2, phoneCode: selectedIsdCodeOption2?.countryCode || 'GB', },
		});

		Store.commit.register.updateCustomerDetails(userDetail);
		Store.commit.showSpinner();

		this.$selfServeApi.registerAccountUserRegistrationForLinearRegistrationCreate({
			customerNumber: userDetail.customerNumber,
			lastName: userDetail.customerLastName,
		})
			.then((data) => {
				Store.commit.register.updateRegistrationDetails({
					...data.result,
				});

				// eslint-disable-next-line @typescript-eslint/no-empty-function
				return this.$router.push('./check-details').catch(() => { });
			})
			.catch((error) => {
				if (isBadRequestResult(error) && Array.isArray(error.errors) && error.errors[0]?.message) {
					switch (error.errors[0].metadata.Number) {
						case 'E031' :
							this.customCustomerNumError = true;
							return;
						default :
							this.apiError = error.errors[0].message;
							return;
					}
				}
				this.apiError = `Please wait a few minutes and try again. If the problem persists, please <a target="_blank" href=${this.contactUsLocation}>contact us</a>.`;
			}).finally(() => Store.commit.hideSpinner());
	}

	focusRef(ref: string) {
		(this.$refs[ref] as HTMLElement)?.focus();
	}

	focusRefs(refs: string[]) {
		if (refs.length < 1) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-this-alias
		let currentRef: Vue = this;
		for (const ref of refs.slice(0, -1)) {
			currentRef = currentRef?.$refs[ref] as Vue;
		}
		(currentRef?.$refs[refs[refs.length - 1]] as HTMLElement)?.focus();
	}

	handleNumberOfOccupants(e:Event) {
		const selectedText = (e.target as HTMLSelectElement);
		this.personsToDisplayText = selectedText.options[selectedText.selectedIndex].text;
		this.$v.numberOfOccupants.$touch();
	}

	get lastName() {
		return Store.state.findCustomerNumber.customerLastName;
	}
}
